import Link from 'next/link'
import { useEffect, useState } from 'react'
import { segment } from 'src/lib/Segments'

import CustomDatePicker from '@components/generic/CustomDatePicker'
import { BiRightArrowAlt } from '@react-icons/all-files/bi/BiRightArrowAlt'

const RENEW_TIME_OPTIONS = [
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
]

interface RentCalculatorProps {
  onCalculate?: (result: { rent: number; newRent: number; increase: number }) => void
}

const RentCalculator = ({ onCalculate }: RentCalculatorProps) => {
  const [rent, setRent] = useState<string>('')
  const [leaseEndDate, setLeaseEndDate] = useState<Date | null>(null)
  const [renewTime, setRenewTime] = useState<{ value: string; label: string } | null>(null)
  const [rentText, setRentText] = useState<JSX.Element | null>(null)

  useEffect(() => {
    if (!leaseEndDate || !rent || !renewTime) {
      setRentText(null)
      return
    }

    const rentNumber = parseFloat(rent)
    let newRent = 0
    let increasePercentage = 0

    if (
      new Date('2022-10-01').getTime() < leaseEndDate.getTime() &&
      leaseEndDate.getTime() < new Date('2023-09-30').getTime()
    ) {
      increasePercentage = renewTime.value === '1' ? 0.0325 : 0.05
    } else if (
      new Date('2023-10-01').getTime() < leaseEndDate.getTime() &&
      leaseEndDate.getTime() < new Date('2024-09-30').getTime()
    ) {
      if (renewTime.value === '1') {
        increasePercentage = 0.03
      } else {
        // For 2-year renewal in this period, show year 1 and year 2
        const year1Rent = rentNumber * (1 + 0.0275)
        const year2Rent = year1Rent * (1 + 0.032)
        setRentText(
          <div className="grid gap-6 md:grid-cols-2">
            <div className="flex flex-col items-center justify-center">
              <span>Your new rent for year 1 should be</span>
              <span className="font-semibold">${year1Rent.toFixed(2)}</span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <span>Your new rent for year 2 should be</span>
              <span className="font-semibold">${year2Rent.toFixed(2)}</span>
            </div>
          </div>
        )
        onCalculate?.({ rent: rentNumber, newRent: year2Rent, increase: year2Rent - rentNumber })
        return
      }
    } else if (
      new Date('2024-10-01').getTime() < leaseEndDate.getTime() &&
      leaseEndDate.getTime() < new Date('2025-09-30').getTime()
    ) {
      increasePercentage = renewTime.value === '1' ? 0.0275 : 0.0525
    } else if (
      new Date('2025-10-01').getTime() < leaseEndDate.getTime() &&
      leaseEndDate.getTime() < new Date('2026-09-30').getTime()
    ) {
      increasePercentage = renewTime.value === '1' ? 0.03 : 0.045
    }

    if (increasePercentage > 0) {
      newRent = rentNumber * (1 + increasePercentage)
      setRentText(
        <>
          <span>Your new rent should be</span>
          <span className="font-semibold">${newRent.toFixed(2)}</span>
        </>
      )
      onCalculate?.({ rent: rentNumber, newRent, increase: newRent - rentNumber })
    }
  }, [leaseEndDate, rent, renewTime, onCalculate])

  return (
    <div className="rounded-2xl bg-white p-2 shadow-sm md:p-6">
      <div className="space-y-6">
        {/* Rent Input */}
        <div className="space-y-2">
          <label className="text-base font-semibold text-[#253858]">What is your rent now?</label>
          <div className="relative">
            {rent && (
              <span className="absolute left-4 top-1/2 -translate-y-1/2 pt-0.5 pl-0.5 text-base text-[#061938]">
                $
              </span>
            )}
            <input
              className={`h-10 w-full rounded-3xl border border-[#DFE1E6] px-4 text-base text-[#061938] placeholder-[#5E6C84] focus:border-primary focus:outline-none ${
                rent ? 'pl-7' : ''
              }`}
              placeholder="$ Current monthly rent"
              type="text"
              value={rent}
              onClick={() => segment?.explore_rent_calculator()}
              onChange={(e) => {
                const inputValue = e.target.value
                if (/^\d*\.?\d{0,2}$/.test(inputValue) || inputValue === '') {
                  setRent(inputValue)
                }
              }}
            />
          </div>
        </div>

        {/* Date Picker */}
        <div className="space-y-2">
          <label className="text-base font-semibold text-[#061938]">
            When does your lease end?
          </label>
          <CustomDatePicker
            selectedDate={leaseEndDate}
            onChange={(e) => {
              setLeaseEndDate(e)
              segment?.explore_rent_calculator()
            }}
            minDate={new Date('2022-10-01')}
            maxDate={new Date('2026-09-30')}
            placeholderText="MM / DD / YYYY"
            inputStyle="!h-10 !rounded-3xl !border-[#DFE1E6] !text-base !text-[#061938] placeholder-[#5E6C84]"
          />
        </div>

        {/* Renewal Options */}
        <div className="space-y-2">
          <label className="text-base font-semibold text-[#061938]">
            Will you renew for 1 or 2 years?
          </label>
          <div className="flex gap-6">
            {RENEW_TIME_OPTIONS.map((option) => (
              <div
                key={option.value}
                className={`flex h-10 w-full cursor-pointer items-center justify-center rounded-lg border px-4 text-sm ${
                  renewTime?.value === option.value
                    ? 'border-primary bg-blue-25 text-[#061938]'
                    : 'border-[#DFE1E6] bg-white text-[#061938]'
                }`}
                onClick={() => {
                  segment?.explore_rent_calculator()
                  setRenewTime(option)
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      {rentText && (
        <div className="mt-6 overflow-hidden rounded-xl">
          <div className="flex flex-col items-center justify-center space-y-2 bg-blue-25 p-4 text-[#253858]">
            {rentText}
          </div>
          <Link
            passHref
            href={`/listings?q=${encodeURI(JSON.stringify({ isBuildingRentStabilized: true }))}`}
          >
            <a
              className="hidden items-center justify-center space-x-1 bg-light-10 py-3 font-light text-bluegray-300 sm:flex"
              onClick={() => segment?.explore_calculator_rent_stabilized_listings()}
            >
              <u>Check out listings in rent-stabilized buildings</u>
              <BiRightArrowAlt size={22} />
            </a>
          </Link>
        </div>
      )}
    </div>
  )
}

export default RentCalculator
